import { useId, useState } from "react";
import {
  FIELD_TYPES,
  FIELD_TYPE_LABELS,
  MAX_FIELDS_PER_ITEM,
  linkHref,
  type FieldInput,
  type FieldTemplate,
  type FieldType,
  type FieldVisibility,
} from "@iltocat/shared";
import type { Vocabulary } from "../lib/useVocabulary.js";
import { Icon } from "./Icon.js";

/**
 * The item's modular fields: a label, a value, and -- the part that matters --
 * whether it is public or private.
 *
 * **Visibility is on every row and it is never hidden.** A private field is
 * what stays out of a Stage 3 prompt and a Stage 4 listing, so a control that
 * decides that has to be somewhere a person will see it being decided rather
 * than in a menu. Each row says which it is in words, not just an icon.
 *
 * A new field is a row of choices: name, value, type, visibility, and whether
 * to keep it as a template. Once the item is saved those are made, so a saved
 * field shows its name as the value's label, with visibility, rename and
 * remove beside it. Its type is fixed, since a value typed as a date means
 * nothing as a number, and it is not offered as a template any more. A saved
 * link field also gets an Open button, which opens the address in a new tab.
 *
 * Starting a field from a template fills the label, type, group and visibility
 * and leaves the value for the person -- which is most of the work of the
 * fortieth drill. "Save as template" goes the other way.
 *
 * A field carries a group and it survives a round trip, but only templates use
 * it: it files them under headings in the picker once there are too many for
 * chips. So it is asked for when saving a template, not on every row.
 */

export interface FieldDraft {
  /** A local key so React can keep a row's focus while it is edited. Never sent. */
  key: string;
  label: string;
  value: string;
  type: FieldType;
  group: string;
  visibility: FieldVisibility;
  /**
   * The field is already on the saved item. Its type is then fixed and it is
   * not offered as a template; its name, value and visibility stay editable.
   */
  saved: boolean;
}

/** Past this many unused templates, the rest go behind a searchable list. */
const CHIP_LIMIT = 6;

let counter = 0;
export const newFieldKey = (): string => `f${(counter += 1)}`;

export const blankField = (over: Partial<FieldDraft> = {}): FieldDraft => ({
  key: newFieldKey(),
  label: "",
  value: "",
  type: "text",
  group: "",
  visibility: "public",
  saved: false,
  ...over,
});

/** What the worker is sent: no local key, and an unlabelled row is not a field. */
export const fieldsToRequest = (fields: FieldDraft[]): FieldInput[] =>
  fields
    .filter((field) => field.label.trim() !== "")
    .map(({ label, value, type, group, visibility }) => ({ label, value, type, group, visibility }));

export function FieldsEditor({
  fields,
  onChange,
  vocabulary,
  builtIn = [],
}: {
  fields: FieldDraft[];
  onChange: (fields: FieldDraft[]) => void;
  vocabulary: Vocabulary;
  /** Labels the form already has a box of its own for, so no template offers them again. */
  builtIn?: string[];
}) {
  const [note, setNote] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [query, setQuery] = useState("");
  // The row being turned into a template, and the group it is being filed under.
  const [saving, setSaving] = useState<{ key: string; group: string } | null>(null);
  // The saved field whose name is being edited, and the name it had, for Escape
  // and for a name cleared to nothing.
  const [renaming, setRenaming] = useState<{ key: string; was: string } | null>(null);
  const labelsId = useId();
  const groupsId = useId();
  const valuesId = useId();

  const update = (key: string, changes: Partial<FieldDraft>) =>
    onChange(fields.map((field) => (field.key === key ? { ...field, ...changes } : field)));

  // A blank name would drop the field on save (an unlabelled row is not a
  // field), so clearing the name puts the old one back instead.
  const finishRename = () => {
    if (!renaming) return;
    const field = fields.find((entry) => entry.key === renaming.key);
    if (field && field.label.trim() === "") update(field.key, { label: renaming.was });
    setRenaming(null);
  };

  const add = (template: FieldTemplate | null) => {
    onChange([
      ...fields,
      template
        ? blankField({
            label: template.label,
            type: template.type,
            group: template.group,
            visibility: template.visibility,
          })
        : blankField(),
    ]);
    setBrowsing(false);
    setQuery("");
  };

  const has = (label: string, list: { label: string }[]) =>
    list.some((entry) => entry.label.trim().toLowerCase() === label.trim().toLowerCase());
  // A template already on this item has nothing left to offer, and a field
  // that is already a template does not need saving as one again.
  const taken = [...fields, ...builtIn.map((label) => ({ label }))];
  const unused = vocabulary.templates.filter((template) => !has(template.label, taken));
  const chips = unused.slice(0, CHIP_LIMIT);
  const rest = unused.slice(CHIP_LIMIT);
  const groups = [...new Set(vocabulary.templates.map((template) => template.group).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b),
  );

  async function saveTemplate(field: FieldDraft, group: string) {
    if (!field.label.trim()) return;
    setSaving(null);
    try {
      await vocabulary.saveTemplate({
        label: field.label,
        type: field.type,
        group: group.trim(),
        visibility: field.visibility,
      });
      setNote(`Saved “${field.label.trim()}” as a template.`);
    } catch {
      setNote("That template did not save.");
    }
  }

  return (
    <div className="field">
      <span className="field-label">Fields</span>

      {fields.length === 0 && (
        <p className="muted small">Serial number, purchase date, dimensions: anything worth finding it by.</p>
      )}

      <ul className="fields">
        {fields.map((field) => {
          const visibility = (
            <button
              type="button"
              className={field.visibility === "private" ? "chip chip-small chip-private" : "chip chip-small"}
              aria-pressed={field.visibility === "private"}
              title={
                field.visibility === "private"
                  ? "Private: kept out of listings and anything sent to an AI."
                  : "Public: may appear in a listing."
              }
              onClick={() =>
                update(field.key, { visibility: field.visibility === "private" ? "public" : "private" })
              }
            >
              <Icon name={field.visibility === "private" ? "lock" : "globe"} size={14} />
              {field.visibility === "private" ? "Private" : "Public"}
            </button>
          );
          const remove = (
            <button
              type="button"
              className="icon-button icon-button-sm icon-button-danger"
              aria-label={`Remove ${field.label || "field"}`}
              title="Remove field"
              onClick={() => onChange(fields.filter((entry) => entry.key !== field.key))}
            >
              <Icon name="trash" size={16} />
            </button>
          );
          const valueInput = (
            <input
              id={`${valuesId}-${field.key}`}
              className="input"
              aria-label={field.saved ? undefined : `${field.label || "Field"} value`}
              placeholder={field.type === "url" ? "example.com/manual" : "Value"}
              // A link is typed as text: `type="url"` refuses "example.com"
              // without its https://, which is how people write an address.
              type={field.type === "date" ? "date" : "text"}
              inputMode={field.type === "number" ? "decimal" : field.type === "url" ? "url" : undefined}
              value={field.value}
              onChange={(event) => update(field.key, { value: event.target.value })}
            />
          );

          if (field.saved) {
            const editing = renaming?.key === field.key;
            // Only http and https come back from linkHref: a value anybody with
            // edit access typed is never a `javascript:` href.
            const href = field.type === "url" ? linkHref(field.value) : null;
            return (
              <li key={field.key} className="field-row">
                <div className="field-head">
                  {editing ? (
                    <input
                      className="input input-small field-rename"
                      aria-label="Field name"
                      autoFocus
                      value={field.label}
                      onChange={(event) => update(field.key, { label: event.target.value })}
                      onBlur={finishRename}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          finishRename();
                        } else if (event.key === "Escape") {
                          update(field.key, { label: renaming.was });
                          setRenaming(null);
                        }
                      }}
                    />
                  ) : (
                    <label className="field-name" htmlFor={`${valuesId}-${field.key}`}>
                      {field.label}
                    </label>
                  )}
                  <div className="field-tools">
                    {href && (
                      <a
                        className="button button-quiet button-small"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={href}
                      >
                        <Icon name="external" size={14} />
                        Open<span className="sr-only"> {field.label}</span>
                      </a>
                    )}
                    {visibility}
                    {!editing && (
                      <button
                        type="button"
                        className="icon-button icon-button-sm"
                        aria-label={`Rename ${field.label}`}
                        title="Rename"
                        onClick={() => setRenaming({ key: field.key, was: field.label })}
                      >
                        <Icon name="pencil" size={16} />
                      </button>
                    )}
                    {remove}
                  </div>
                </div>
                {valueInput}
              </li>
            );
          }

          return (
            <li key={field.key} className="field-row">
              <div className="field-main">
                <input
                  className="input"
                  aria-label="Field name"
                  placeholder="Name"
                  list={labelsId}
                  value={field.label}
                  onChange={(event) => {
                    const label = event.target.value;
                    // Typing a template's exact name adopts its type and
                    // visibility, so "Serial number" comes back private without
                    // the person having to say so again.
                    const match = vocabulary.templates.find(
                      (template) => template.label.toLowerCase() === label.trim().toLowerCase(),
                    );
                    update(
                      field.key,
                      match && field.label === "" && field.value === ""
                        ? { label, type: match.type, group: match.group, visibility: match.visibility }
                        : { label },
                    );
                  }}
                />
                {valueInput}
              </div>
              <div className="field-meta">
                <select
                  className="input input-small"
                  aria-label={`${field.label || "Field"} type`}
                  value={field.type}
                  onChange={(event) => update(field.key, { type: event.target.value as FieldType })}
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {FIELD_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
                {visibility}
                {field.label.trim() !== "" && !has(field.label, vocabulary.templates) && (
                  <button
                    type="button"
                    className="button button-ghost button-small"
                    onClick={() => setSaving({ key: field.key, group: field.group })}
                  >
                    <Icon name="bookmark" size={14} />
                    Save as template
                  </button>
                )}
                {remove}
              </div>
              {saving?.key === field.key && (
                <form
                  className="field-meta"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveTemplate(field, saving.group);
                  }}
                >
                  <input
                    className="input input-small"
                    aria-label="Template group (optional)"
                    placeholder="Group (optional)"
                    list={groupsId}
                    autoFocus
                    value={saving.group}
                    onChange={(event) => setSaving({ key: field.key, group: event.target.value })}
                  />
                  <button type="submit" className="button button-small">
                    Save template
                  </button>
                  <button
                    type="button"
                    className="button button-ghost button-small"
                    onClick={() => setSaving(null)}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </li>
          );
        })}
      </ul>

      <datalist id={groupsId}>
        {groups.map((group) => (
          <option key={group} value={group} />
        ))}
      </datalist>

      <datalist id={labelsId}>
        {vocabulary.templates.map((template) => (
          <option key={template.id} value={template.label} />
        ))}
      </datalist>

      {fields.length < MAX_FIELDS_PER_ITEM && (
        <div className="chips">
          <button type="button" className="chip chip-ghost" onClick={() => add(null)}>
            <Icon name="plus" size={14} />
            Add a field
          </button>
          {chips.map((template) => (
            <button key={template.id} type="button" className="chip" onClick={() => add(template)}>
              <Icon name={template.visibility === "private" ? "lock" : "plus"} size={14} />
              {template.label}
            </button>
          ))}
          {rest.length > 0 && (
            <button
              type="button"
              className="chip chip-ghost"
              aria-expanded={browsing}
              onClick={() => setBrowsing(!browsing)}
            >
              {browsing ? "Fewer" : `${rest.length} more…`}
            </button>
          )}
        </div>
      )}

      {browsing && rest.length > 0 && fields.length < MAX_FIELDS_PER_ITEM && (
        <TemplatePicker templates={rest} query={query} onQuery={setQuery} onPick={add} />
      )}

      {note && <p className="muted small">{note}</p>}

      {vocabulary.templates.length > 0 && (
        <details className="more">
          <summary>
            <Icon name="chevron-right" size={16} />
            Your templates
          </summary>
          <ul className="templates">
            {vocabulary.templates.map((template) => (
              <li key={template.id}>
                <span>
                  {template.label} · {FIELD_TYPE_LABELS[template.type]} · {template.visibility}
                </span>
                <button
                  type="button"
                  className="button button-ghost button-small"
                  aria-label={`Delete template ${template.label}`}
                  onClick={() => void vocabulary.removeTemplate(template.id)}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/**
 * Every template that did not fit as a chip, filterable by typing and filed
 * under its group. With no groups in use it is just a filtered list, without
 * headings.
 */
function TemplatePicker({
  templates,
  query,
  onQuery,
  onPick,
}: {
  templates: FieldTemplate[];
  query: string;
  onQuery: (query: string) => void;
  onPick: (template: FieldTemplate) => void;
}) {
  const needle = query.trim().toLowerCase();
  const shown = templates.filter(
    (template) =>
      needle === "" ||
      template.label.toLowerCase().includes(needle) ||
      template.group.toLowerCase().includes(needle),
  );
  const named = [...new Set(shown.map((template) => template.group))].sort((a, b) => {
    // The ungrouped go last.
    if (a === "" || b === "") return a === "" ? 1 : -1;
    return a.localeCompare(b);
  });
  const headed = named.some((group) => group !== "");

  return (
    <div className="template-picker">
      <input
        className="input"
        type="search"
        aria-label="Search templates"
        placeholder="Search templates"
        autoFocus
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      {shown.length === 0 && <p className="muted small">No template matches that.</p>}
      <div className="template-picker-list">
        {named.map((group) => (
          <section key={group} className="template-group">
            {headed && <h4 className="template-group-title">{group || "Other"}</h4>}
            <div className="chips">
              {shown
                .filter((template) => template.group === group)
                .map((template) => (
                  <button key={template.id} type="button" className="chip" onClick={() => onPick(template)}>
                    <Icon name={template.visibility === "private" ? "lock" : "plus"} size={14} />
                    {template.label}
                  </button>
                ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
